import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Star, MapPin, Users, Sparkles, Calendar, TrendingUp, QrCode, DollarSign, Loader2, Music,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar,
} from "recharts";
import { api } from "../lib/api";
import { Stat, StatusBadge, Pill } from "../components/shared";
import { useLang } from "../lib/i18n";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";

const tooltipStyle = { background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 2, color: "#fff" };
const trend = [
  { d: "Mon", tips: 40, scans: 120 }, { d: "Tue", tips: 55, scans: 150 },
  { d: "Wed", tips: 30, scans: 90 }, { d: "Thu", tips: 80, scans: 210 },
  { d: "Fri", tips: 160, scans: 380 }, { d: "Sat", tips: 210, scans: 460 }, { d: "Sun", tips: 120, scans: 300 },
];

export default function RestaurantDashboard() {
  const { t } = useLang();
  const d = t.dash.r;
  const [venues, setVenues] = useState([]);
  const [venue, setVenue] = useState(null);
  const [artists, setArtists] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [filters, setFilters] = useState({ style: "", city: "" });
  const [aiMatches, setAiMatches] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    api.venues().then((v) => { setVenues(v); setVenue(v[0]); });
  }, []);

  useEffect(() => {
    if (!venue) return;
    api.venueAnalytics(venue.id).then(setAnalytics);
    api.bookings({ venue_id: venue.id }).then(setBookings);
    setAiMatches(null);
  }, [venue]);

  useEffect(() => {
    const params = {};
    if (filters.style) params.style = filters.style;
    if (filters.city) params.city = filters.city;
    api.artists(params).then(setArtists);
  }, [filters]);

  const runMatch = async () => {
    setAiLoading(true);
    try {
      const res = await api.aiMatch(venue.id);
      setAiMatches(res.matches || []);
      toast.success("Demo matches ready");
    } catch { toast.error("Matching failed"); }
    setAiLoading(false);
  };

  const styles = ["Jazz & Soul", "Acoustic & Folk", "House & Electronic", "Pop & R&B", "Gypsy Jazz"];
  const cities = ["London", "Manchester"];

  if (!venue) return <Loading />;

  return (
    <div className="max-w-7xl mx-auto px-5 py-10">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] gold mb-2">{d.eyebrow}</p>
          <h1 className="font-serif-lux text-4xl sm:text-5xl font-light">{venue.name}</h1>
          <p className="text-neutral-500 text-sm mt-2 flex items-center gap-4">
            <span className="flex items-center gap-1"><MapPin size={14} /> {venue.city}</span>
            <span className="flex items-center gap-1"><Users size={14} /> {venue.capacity} {d.seats}</span>
            <span className="flex items-center gap-1"><Music size={14} /> {venue.music_style}</span>
            <span className="flex items-center gap-1"><Star size={14} className="gold" /> {venue.rating}</span>
          </p>
        </div>
        <div className="flex gap-2">
          {venues.map((v) => (
            <Pill key={v.id} active={v.id === venue.id} onClick={() => setVenue(v)} testid={`venue-tab-${v.id}`}>
              {v.name}
            </Pill>
          ))}
        </div>
      </div>

      {/* Stats */}
      {analytics && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          <Stat testid="stat-bookings" label={d.bookings} value={analytics.booking_count} sub={`${analytics.confirmed} ${d.confirmed}`} />
          <Stat testid="stat-qr" label={d.qrScans} value={analytics.qr_scans} sub={d.thisMonth} />
          <Stat testid="stat-tips" label={d.tipsGenerated} value={`£${analytics.tips_generated}`} sub={d.guestTips} />
          <Stat testid="stat-reach" label={d.socialReach} value={analytics.social_reach.toLocaleString()} sub={d.impressions} />
        </div>
      )}

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-4 mb-12">
        <div className="card-lux p-6">
          <p className="text-sm text-neutral-400 mb-4 flex items-center gap-2"><TrendingUp size={16} className="gold" /> Guest engagement & QR scans</p>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={trend}>
              <defs>
                <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#D4AF37" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeOpacity={0.1} vertical={false} />
              <XAxis dataKey="d" stroke="#666" fontSize={12} /><YAxis stroke="#666" fontSize={12} />
              <Tooltip contentStyle={tooltipStyle} />
              <Area type="monotone" dataKey="scans" stroke="#D4AF37" fill="url(#g1)" strokeWidth={2} isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="card-lux p-6">
          <p className="text-sm text-neutral-400 mb-4 flex items-center gap-2"><DollarSign size={16} className="gold" /> Tips generated (£)</p>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={trend}>
              <CartesianGrid strokeOpacity={0.1} vertical={false} />
              <XAxis dataKey="d" stroke="#666" fontSize={12} /><YAxis stroke="#666" fontSize={12} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
              <Bar dataKey="tips" fill="#D4AF37" radius={[2, 2, 0, 0]} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* AI Match */}
      <div className="card-lux p-8 mb-10 gold-glow">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] gold mb-2 flex items-center gap-2"><Sparkles size={14} /> {d.aiMatching}</p>
            <h3 className="font-serif-lux text-2xl font-light">Find the perfect act for {venue.name}</h3>
            <p className="text-sm text-neutral-500 mt-1">Our AI ranks artists by audience, style and venue fit.</p>
          </div>
          <button data-testid="ai-match-btn" onClick={runMatch} disabled={aiLoading} className="btn-gold px-6 py-3 rounded-sm text-sm font-semibold flex items-center gap-2">
            {aiLoading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
            {d.runMatch}
          </button>
        </div>
        {aiMatches && (
          <div className="grid md:grid-cols-3 gap-4 mt-6" data-testid="ai-match-results">
            {aiMatches.map((m) => (
              <div key={m.artist_id} className="border border-gold/40 bg-gold/5 p-5 rounded-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold">{m.name}</span>
                  <span className="gold font-serif-lux text-2xl">{m.score}</span>
                </div>
                <p className="text-xs text-neutral-400">{m.reason}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Browse artists */}
      <div className="mb-6">
        <p className="text-xs uppercase tracking-[0.3em] gold mb-3">{d.browseArtists}</p>
        <div className="flex flex-wrap gap-2 mb-6">
          <Pill active={!filters.style} onClick={() => setFilters({ ...filters, style: "" })} testid="filter-style-all">{d.allStyles}</Pill>
          {styles.map((s) => (
            <Pill key={s} active={filters.style === s} onClick={() => setFilters({ ...filters, style: s })} testid={`filter-style-${s}`}>{s}</Pill>
          ))}
          <span className="w-px bg-white/10 mx-1" />
          <Pill active={!filters.city} onClick={() => setFilters({ ...filters, city: "" })} testid="filter-city-all">{d.allCities}</Pill>
          {cities.map((c) => (
            <Pill key={c} active={filters.city === c} onClick={() => setFilters({ ...filters, city: c })} testid={`filter-city-${c}`}>{c}</Pill>
          ))}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {artists.map((a) => (
          <ArtistCard key={a.id} artist={a} venue={venue} onBooked={() => api.bookings({ venue_id: venue.id }).then(setBookings)} />
        ))}
      </div>

      {/* Bookings */}
      <div className="mt-14">
        <p className="text-xs uppercase tracking-[0.3em] gold mb-4">{d.yourBookings}</p>
        <div className="card-lux divide-y divide-white/10" data-testid="venue-bookings-list">
          {bookings.length === 0 && <p className="p-6 text-neutral-500 text-sm">{d.noBookings}</p>}
          {bookings.map((b) => {
            const artist = artists.find((x) => x.id === b.artist_id);
            return (
              <div key={b.id} className="flex items-center justify-between p-5">
                <div className="flex items-center gap-3">
                  <Calendar size={16} className="gold" />
                  <div>
                    <p className="text-sm font-medium">{artist?.name || b.artist_id} · {b.shift}</p>
                    <p className="text-xs text-neutral-500">{b.date} · £{b.fee}</p>
                  </div>
                </div>
                <StatusBadge status={b.status} />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function ArtistCard({ artist, venue, onBooked }) {
  const { t } = useLang();
  const d = t.dash.r;
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState("");
  const [shift, setShift] = useState("Evening");
  const [predict, setPredict] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadPredict = async () => {
    setLoading(true);
    try { setPredict(await api.aiPredict(venue.id, artist.id)); } catch {}
    setLoading(false);
  };

  const book = async () => {
    if (!date) { toast.error("Pick a date"); return; }
    await api.createBooking({ venue_id: venue.id, artist_id: artist.id, date, shift, fee: artist.hourly_rate * 2 });
    toast.success(`Demo booking saved for ${artist.name}`);
    setOpen(false); onBooked();
  };

  return (
    <div className="card-lux overflow-hidden hover:border-white/25 transition-colors" data-testid={`artist-card-${artist.id}`}>
      <div className="h-52 overflow-hidden">
        <img src={artist.photo} alt={artist.name} className="w-full h-full object-cover" />
      </div>
      <div className="p-5">
        <div className="flex items-center justify-between">
          <h3 className="font-serif-lux text-xl">{artist.name}</h3>
          <span className="flex items-center gap-1 text-sm gold"><Star size={14} /> {artist.rating}</span>
        </div>
        <p className="text-xs text-neutral-500 mt-1">{artist.style} · {artist.city}</p>
        <p className="text-xs text-neutral-400 mt-3 line-clamp-2">{artist.bio}</p>
        <div className="flex items-center justify-between mt-4">
          <span className="text-sm text-neutral-400">£{artist.hourly_rate}/hr · {(artist.followers / 1000).toFixed(1)}k</span>
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) loadPredict(); }}>
            <DialogTrigger asChild>
              <button data-testid={`book-btn-${artist.id}`} className="btn-gold px-4 py-2 rounded-sm text-xs font-semibold">{d.book}</button>
            </DialogTrigger>
            <DialogContent className="bg-[#0f0f0f] border-white/10 text-white">
              <DialogHeader><DialogTitle className="font-serif-lux text-2xl font-light">Book {artist.name}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <label className="text-xs text-neutral-400">Date</label>
                  <input data-testid="booking-date" type="date" value={date} onChange={(e) => setDate(e.target.value)}
                    className="w-full mt-1 bg-transparent border border-white/20 rounded-sm px-3 py-2 text-sm focus:border-gold outline-none" />
                </div>
                <div>
                  <label className="text-xs text-neutral-400">Shift</label>
                  <div className="flex gap-2 mt-1">
                    {["Lunch", "Evening", "Night", "Weekend"].map((s) => (
                      <Pill key={s} active={shift === s} onClick={() => setShift(s)} testid={`shift-${s}`}>{s}</Pill>
                    ))}
                  </div>
                </div>
                <div className="border border-gold/30 bg-gold/5 p-4 rounded-sm">
                  <p className="text-xs uppercase tracking-widest gold mb-2 flex items-center gap-2"><Sparkles size={12} /> AI Prediction · Demo</p>
                  {loading || !predict ? <p className="text-xs text-neutral-500">Analysing…</p> : (
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div><p className="gold font-serif-lux text-xl">£{predict.predicted_tips}</p><p className="text-[10px] text-neutral-500">Est. tips</p></div>
                      <div><p className="gold font-serif-lux text-xl">{predict.predicted_engagement_rate}%</p><p className="text-[10px] text-neutral-500">Engagement</p></div>
                      <div><p className="gold font-serif-lux text-xl">{predict.booking_success}</p><p className="text-[10px] text-neutral-500">Fit score</p></div>
                    </div>
                  )}
                </div>
                <p className="text-sm text-neutral-400">Estimated fee: <span className="gold">£{artist.hourly_rate * 2}</span> (2h set)</p>
              </div>
              <DialogFooter>
                <button data-testid="confirm-booking" onClick={book} className="btn-gold px-6 py-2.5 rounded-sm text-sm font-semibold w-full">Simulate booking request</button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
}

function Loading() {
  return <div className="h-[70vh] flex items-center justify-center"><Loader2 className="animate-spin gold" size={32} /></div>;
}
